if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
        
        // Add resize listener for responsive toggle button positioning
        this.resizeHandler = this.positionToggleButton.bind(this);
        window.addEventListener('resize', this.resizeHandler);
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
        
        // Reset toggle button position to default
        this.resetToggleButtonPosition();

        this.classList.remove('has-success-popup');
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        // Get product URL and extract product handle
        const productUrl = opener.getAttribute('data-product-url');
        const productHandle = this.extractProductHandle(productUrl);

        // Fetch only the product data we need
        this.fetchProductData(productHandle)
          .then((productData) => {
            const productHTML = this.createProductHTML(productData);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productHTML);
            
            // Add event listeners after HTML is inserted
            this.addEventListeners(productData);

            super.show(opener);
            
            // Position toggle button based on modal height (desktop only)
            this.positionToggleButton();
          })
          .catch((error) => {
            console.error('Error fetching product data:', error);
            // Fallback to original method if API fails
            this.fallbackToFullPage(productUrl, opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      extractProductHandle(productUrl) {
        // Extract product handle from URL like /products/product-handle
        const match = productUrl.match(/\/products\/([^\/\?]+)/);
        return match ? match[1] : null;
      }

      async fetchProductData(productHandle) {
        if (!productHandle) {
          throw new Error('Could not extract product handle');
        }

        // Use Shopify's product API endpoint
        const response = await fetch(`/products/${productHandle}.js`);
        if (!response.ok) {
          throw new Error(`Failed to fetch product data: ${response.status}`);
        }

        const productData = await response.json();
        
        // Debug logging to see the structure
        console.log('Product API Response:', productData);
        console.log('Variants:', productData.variants);
        console.log('Images:', productData.images);
        console.log('Options:', productData.options);
        
        return productData;
      }

      createProductHTML(productData) {
        const { title, featured_image, images, variants, options } = productData;
        const imageUrl = this.extractImageUrl(featured_image) || (images && images.length > 0 ? this.extractImageUrl(images[0]) : '');
        const selectedVariant = variants[0]; // Default to first variant

        // Generate swatch options HTML
        const swatchesHTML = this.generateSwatchesHTML(options, variants, selectedVariant, images);
        
        // Generate add to cart button
        const addToCartHTML = this.generateAddToCartHTML(selectedVariant);

        return `
          <div class="quick-add-product-info">
            <div class="quick-add-product-header">
              <div class="quick-add-product-image">
                ${imageUrl ? `<img src="${imageUrl}" alt="${title}" loading="lazy">` : ''}
              </div>
              <div class="quick-add-product-details">
                <h2 class="quick-add-product-title">${title}</h2>
                <div class="quick-add-product-price">
                  <span class="price-item price-item--regular">
                    ${this.formatMoney(selectedVariant.price)}
                  </span>
                  ${selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price ? 
                    `<span class="price-item price-item--sale">
                      ${this.formatMoney(selectedVariant.compare_at_price)}
                    </span>` : ''
                  }
                </div>
              </div>
            </div>
            <div class="quick-add-product-separator"></div>
            <div class="quick-add-product-options">
              ${swatchesHTML}
              ${addToCartHTML}
            </div>
          </div>
        `;
      }

      generateSwatchesHTML(options, variants, selectedVariant, productImages) {
        if (!options || options.length === 0) return '';

        // Create a mapping of variant images
        const variantImageMap = this.createVariantImageMap(variants, productImages);
        
        let swatchesHTML = '';
        
        options.forEach((option, optionIndex) => {
          const optionName = option.name;
          const optionValues = option.values;
          const selectedValue = selectedVariant.options[optionIndex];
          
          swatchesHTML += `
            <div class="quick-add-option-group" data-option-index="${optionIndex}">
              <div class="quick-add-option-header">
                <label class="quick-add-option-label">${optionName}</label>
                <span class="quick-add-selected-value">${selectedValue}</span>
              </div>
              <div class="quick-add-swatches">
          `;
          
          optionValues.forEach((value) => {
            const isSelected = selectedVariant.options[optionIndex] === value;
            
            // Find variant with this option value to get its image
            const variantWithImage = this.findVariantWithOption(variants, optionIndex, value);
            let variantImage = this.getVariantImage(variantWithImage, productImages, value);
            
            // If no image found, try the variant image map
            if (!variantImage && variantWithImage) {
              variantImage = variantImageMap[variantWithImage.id];
            }
            
            // Check if this is a color option (common color names)
            const isColorOption = this.isColorOption(optionName);
            
            // Debug logging
            console.log('Option:', optionName, 'Value:', value, 'IsColor:', isColorOption, 'Image:', variantImage);
            
            if (isColorOption) {
              // For color swatches, always show them even if no specific image
              // Use the found image or fallback to first product image
              const displayImage = variantImage || (productImages && productImages.length > 0 ? 
                this.extractImageUrl(productImages[0]) : null);
              
              // Color swatch with image
              swatchesHTML += `
                <button 
                  class="quick-add-swatch quick-add-swatch--color ${isSelected ? 'selected' : ''}" 
                  data-value="${value}"
                  data-option-index="${optionIndex}"
                  data-variant-image="${displayImage || ''}"
                  type="button"
                  aria-label="${value}"
                  ${displayImage ? `style="background-image: url('${displayImage}')"` : ''}
                >
                  <span class="quick-add-swatch-label">${value}</span>
                </button>
              `;
            } else {
              // Regular text swatch
              swatchesHTML += `
                <button 
                  class="quick-add-swatch ${isSelected ? 'selected' : ''}" 
                  data-value="${value}"
                  data-option-index="${optionIndex}"
                  data-variant-image="${variantImage || ''}"
                  type="button"
                  aria-label="${value}"
                >
                  ${value}
                </button>
              `;
            }
          });
          
          swatchesHTML += `
              </div>
            </div>
          `;
        });
        
        return swatchesHTML;
      }

      createVariantImageMap(variants, productImages) {
        const imageMap = {};
        
        if (!productImages || productImages.length === 0) return imageMap;
        
        console.log('Creating variant image map');
        console.log('Variants:', variants);
        console.log('Product images:', productImages);
        
        // Simple approach: assign images to variants based on position
        // This assumes the first few images correspond to the first few variants
        variants.forEach((variant, index) => {
          if (productImages[index]) {
            const imageUrl = this.extractImageUrl(productImages[index]);
            
            if (imageUrl) {
              imageMap[variant.id] = imageUrl;
              console.log(`Mapped variant ${variant.id} to image:`, imageUrl);
            }
          }
        });
        
        // If we have more variants than images, assign the first image to remaining variants
        if (productImages.length > 0) {
          const firstImageUrl = this.extractImageUrl(productImages[0]);
          
          if (firstImageUrl) {
            variants.forEach(variant => {
              if (!imageMap[variant.id]) {
                imageMap[variant.id] = firstImageUrl;
                console.log(`Fallback mapped variant ${variant.id} to first image:`, firstImageUrl);
              }
            });
          }
        }
        
        return imageMap;
      }

      getVariantImage(variant, productImages, optionValue) {
        console.log('getVariantImage called with:', { variant, productImages, optionValue });
        
        if (!variant) {
          console.log('No variant provided');
          return null;
        }
        
        // First try to get image from variant properties
        let variantImage = variant.featured_image || 
                          variant.image || 
                          variant.featured_media?.preview_image?.src ||
                          variant.featured_media?.src;
        
        console.log('Variant image from variant properties:', variantImage);
        
        if (variantImage) {
          // Handle image object structure
          if (typeof variantImage === 'object' && variantImage.src) {
            variantImage = variantImage.src;
          }
          return variantImage;
        }
        
        // If no variant-specific image, try to find image by color name in product images
        if (productImages && productImages.length > 0) {
          console.log('Searching product images for color:', optionValue);
          console.log('Available product images:', productImages);
          
          // Look for images that might contain the color name in alt text or filename
          const colorImage = productImages.find(img => {
            const imgSrc = this.extractImageUrl(img);
            const imgAlt = typeof img === 'object' ? img.alt : '';
            const imgName = imgSrc.toLowerCase();
            const colorName = optionValue.toLowerCase();
            
            console.log('Checking image:', imgSrc, 'against color:', colorName);
            console.log('Image name contains color:', imgName.includes(colorName));
            console.log('Image alt contains color:', imgAlt.toLowerCase().includes(colorName));
            
            return imgName.includes(colorName) || imgAlt.toLowerCase().includes(colorName);
          });
          
          if (colorImage) {
            const imageUrl = this.extractImageUrl(colorImage);
            console.log('Found color image:', imageUrl);
            return imageUrl;
          }
        }
        
        // Fallback to first product image
        if (productImages && productImages.length > 0) {
          const fallbackImage = this.extractImageUrl(productImages[0]);
          console.log('Using fallback image:', fallbackImage);
          return fallbackImage;
        }
        
        console.log('No image found');
        return null;
      }

      extractImageUrl(imageData) {
        if (typeof imageData === 'string') {
          return imageData;
        }
        
        if (typeof imageData === 'object') {
          // Handle different possible image object structures
          return imageData.src || 
                 imageData.url || 
                 imageData.preview_image?.src ||
                 imageData.featured_image ||
                 imageData.image ||
                 null;
        }
        
        return null;
      }

      findVariantWithOption(variants, optionIndex, optionValue) {
        return variants.find(variant => variant.options[optionIndex] === optionValue);
      }

      isColorOption(optionName) {
        const colorKeywords = ['color', 'colour', 'couleur', 'farbe', 'cor', 'colore'];
        return colorKeywords.some(keyword => 
          optionName.toLowerCase().includes(keyword)
        );
      }

      generateAddToCartHTML(selectedVariant) {
        const isAvailable = selectedVariant.available;
        
        return `
          <div class="quick-add-add-to-cart">
            <button 
              class="quick-add-cart-button ${isAvailable ? '' : 'disabled'}"
              type="button"
              data-variant-id="${selectedVariant.id}"
              ${isAvailable ? '' : 'disabled'}
            >
              ${isAvailable ? 'Add to Cart' : 'Sold Out'}
            </button>
          </div>
        `;
      }

      formatMoney(cents) {
        // Simple money formatting - you can customize this based on your theme's currency format
        return `$${(cents / 100).toFixed(2)}`;
      }

      fallbackToFullPage(productUrl, opener) {
        // Fallback to original method if API fails
        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      addEventListeners(productData) {
        const { variants, options } = productData;
        
        // Add swatch click listeners
        const swatches = this.modalContent.querySelectorAll('.quick-add-swatch');
        swatches.forEach(swatch => {
          swatch.addEventListener('click', (e) => {
            this.handleSwatchClick(e, variants, options);
          });
        });

        // Add add to cart button listener
        const addToCartButton = this.modalContent.querySelector('.quick-add-cart-button');
        if (addToCartButton) {
          addToCartButton.addEventListener('click', (e) => {
            this.handleAddToCart(e, productData);
          });
        }
      }

      handleSwatchClick(event, variants, options) {
        const clickedSwatch = event.target;
        const optionIndex = parseInt(clickedSwatch.dataset.optionIndex);
        const selectedValue = clickedSwatch.dataset.value;
        const variantImage = clickedSwatch.dataset.variantImage;

        // Update selected swatch
        const optionGroup = clickedSwatch.closest('.quick-add-option-group');
        optionGroup.querySelectorAll('.quick-add-swatch').forEach(swatch => {
          swatch.classList.remove('selected');
        });
        clickedSwatch.classList.add('selected');

        // Update the selected value display
        const selectedValueSpan = optionGroup.querySelector('.quick-add-selected-value');
        if (selectedValueSpan) {
          selectedValueSpan.textContent = selectedValue;
        }

        // Update main image if this is a color swatch with an image
        if (variantImage && this.isColorOption(this.getOptionName(optionIndex, options))) {
          this.updateMainImage(variantImage);
        }

        // Update selected variant
        const selectedVariant = this.findMatchingVariant(variants, options, optionIndex, selectedValue);
        
        if (selectedVariant) {
          this.updateProductDisplay(selectedVariant);
        }
      }

      getOptionName(optionIndex, options) {
        return options[optionIndex] ? options[optionIndex].name : '';
      }

      updateMainImage(imageUrl) {
        const mainImage = this.modalContent.querySelector('.quick-add-product-image img');
        if (mainImage && imageUrl) {
          mainImage.src = imageUrl;
          mainImage.alt = mainImage.alt; // Keep the same alt text
        }
      }

      findMatchingVariant(variants, options, changedOptionIndex, changedValue) {
        // Get current selections
        const currentSelections = [];
        const swatches = this.modalContent.querySelectorAll('.quick-add-swatch.selected');
        
        swatches.forEach(swatch => {
          const optionIndex = parseInt(swatch.dataset.optionIndex);
          const value = swatch.dataset.value;
          currentSelections[optionIndex] = value;
        });

        // Update with the new selection
        currentSelections[changedOptionIndex] = changedValue;

        // Find matching variant
        return variants.find(variant => {
          return variant.options.every((option, index) => {
            return currentSelections[index] === option;
          });
        });
      }

      updateProductDisplay(selectedVariant) {
        // Update price
        const priceContainer = this.modalContent.querySelector('.quick-add-product-price');
        if (priceContainer) {
          priceContainer.innerHTML = `
            <span class="price-item price-item--regular">
              ${this.formatMoney(selectedVariant.price)}
            </span>
            ${selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price ? 
              `<span class="price-item price-item--sale">
                ${this.formatMoney(selectedVariant.compare_at_price)}
              </span>` : ''
            }
          `;
        }

        // Update add to cart button
        const addToCartButton = this.modalContent.querySelector('.quick-add-cart-button');
        if (addToCartButton) {
          addToCartButton.dataset.variantId = selectedVariant.id;
          addToCartButton.disabled = !selectedVariant.available;
          // addToCartButton.setAttribute("disabled", "disabled");
          addToCartButton.classList.toggle('disabled', !selectedVariant.available);
          addToCartButton.textContent = selectedVariant.available ? 'Add to Cart' : 'Sold Out';
        }
      }

      handleAddToCart(event, productData) {
        const button = event.target;
        const variantId = parseInt(button.dataset.variantId);
        
        if (!variantId || button.disabled) return;

        // Show loading state
        button.disabled = true;
        button.textContent = 'Adding...';

        console.log('Adding variant to cart:', variantId);

        // Add to cart
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1
            }]
          })
        })
        .then(response => response.json())
        .then(data => {
          console.log('Cart add response:', data);
          
          // Success - update cart count and show success popup
          this.updateCartCount();
          this.showSuccessPopup(productData, data);
          
          // Hide variant options
          this.hideVariantOptions();
          console.log("data: ",data);

          this.updateCartDrawer();
          
          // Reset button
          button.textContent = 'Added to Cart';
          setTimeout(() => {
            button.textContent = 'Add to Cart';
            button.disabled = false;
          }, 2000);
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          button.textContent = 'Error - Try Again';
          button.disabled = false;
        });
      }

      updateCartCount() {
        // Update cart count in header
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {

            if(cart.item_count <= 1) {
              const getHeaderCount = document.querySelector(".cart-count-bubble");
              getHeaderCount.classList.add('show');
              getHeaderCount.querySelector('span').innerHTML = cart.item_count;
              console.log('Updating cart drawer...');
              const emptyCartDiv = document.querySelector('.drawer__inner-empty');
              console.log("emptyCartDiv: ", emptyCartDiv);
              if (emptyCartDiv) {
                emptyCartDiv.style.display = 'none';
              }
    
              const showCartFooter = document.querySelector('.drawer__footer');
              console.log("showCartFooter: ", showCartFooter);
              showCartFooter.classList.remove('is-empty');
            }

            const cartCountElements = document.querySelectorAll('[data-cart-count]');
            cartCountElements.forEach(element => {
              element.textContent = cart.item_count;
            });
            
            // Also update any cart icon bubbles
            const cartBubbles = document.querySelectorAll('.cart-count-bubble');
            cartBubbles.forEach(bubble => {
              bubble.textContent = cart.item_count;
            });
            
            // Update drawer count in cart drawer
            const drawerCountElements = document.querySelectorAll('.drawer__count');
            drawerCountElements.forEach(element => {
              element.textContent = cart.item_count;
            });
            
            // Update empty cart count in cart drawer
            const emptyCountElements = document.querySelectorAll('.cart-drawer__empty-count');
            emptyCountElements.forEach(element => {
              element.textContent = cart.item_count;
            });
            
            // Update cart drawer empty state visibility
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              cartDrawer.classList.toggle('is-empty', cart.item_count === 0);
            }
            
            // Update cart-drawer-items empty state
            const cartDrawerItems = document.querySelector('cart-drawer-items');
            if (cartDrawerItems) {
              cartDrawerItems.classList.toggle('is-empty', cart.item_count === 0);
            }
          })
          .catch(error => {
            console.error('Error updating cart count:', error);
          });
      }

      updateCartDrawer() {
        // First, try to use the existing cart-drawer-items component's onCartUpdate method
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
          console.log('Using cart-drawer-items onCartUpdate method');
          cartDrawerItems.onCartUpdate();
          // Also update cart footer and header cart icon
          this.updateCartFooterAndHeader();
          return;
        }
        
        // If that doesn't work, try the pubsub system
        if (window.publish && window.PUB_SUB_EVENTS) {
          console.log('Using pubsub system for cart update');
          publish(PUB_SUB_EVENTS.cartUpdate, { 
            source: 'quick-add', 
            cartData: null 
          });
          // Also update cart footer and header cart icon
          this.updateCartFooterAndHeader();
          return;
        }

        this.hideEmptyCartDiv();
                     
        // Also hide any empty cart elements
        this.hideEmptyCartElements();
        
        // Last resort: manual update using the existing pattern
        console.log('Using manual cart drawer update');
        fetch('/?sections=cart-drawer,cart-icon-bubble,main-cart-footer')
          .then((response) => response.json())
          .then((data) => {
            const drawerContainer = document.querySelector('cart-drawer');
            const drawerHTML = data['cart-drawer'];
            const cartIconHTML = data['cart-icon-bubble'];
            const cartFooterHTML = data['main-cart-footer'];
    
            if (drawerContainer) {
              // Get current cart state to determine if we need full update
              fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                  console.log('Cart state:', cart.item_count, 'items');
                  const isCurrentlyEmpty = cart.item_count === 0;
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = drawerHTML;
                  
                  // If cart is empty, we need to update the entire drawer structure
                  // because empty state has different HTML structure
                  if (isCurrentlyEmpty) {
                    console.log('Updating empty cart drawer');
                    // Update the entire drawer inner content
                    const newDrawerInner = tempDiv.querySelector('.drawer__inner');
                    const existingDrawerInner = drawerContainer.querySelector('.drawer__inner');
                    if (newDrawerInner && existingDrawerInner) {
                      existingDrawerInner.replaceWith(newDrawerInner);
                    }
                    
                    // Update the cart-drawer class to reflect empty state
                    drawerContainer.classList.toggle('is-empty', true);
                    
                                         // Hide the empty cart div by removing it or hiding it
                  } else {
                    console.log('Updating non-empty cart drawer');
                    // For non-empty cart, update specific parts
                    // Update cart-drawer-items
                    const newCartItems = tempDiv.querySelector('cart-drawer-items');
                    const existingCartItems = drawerContainer.querySelector('cart-drawer-items');
                    if (newCartItems && existingCartItems) {
                      existingCartItems.replaceWith(newCartItems);
                    }
                    
                    // Update cart footer
                    const newCartFooter = tempDiv.querySelector('.cart-drawer__footer');
                    const existingCartFooter = drawerContainer.querySelector('.cart-drawer__footer');
                    if (newCartFooter && existingCartFooter) {
                      existingCartFooter.replaceWith(newCartFooter);
                    }
                    
                    // Update drawer header (count)
                    const newDrawerHeader = tempDiv.querySelector('.drawer__header');
                    const existingDrawerHeader = drawerContainer.querySelector('.drawer__header');
                    if (newDrawerHeader && existingDrawerHeader) {
                      existingDrawerHeader.replaceWith(newDrawerHeader);
                    }
                    
                    // Update the cart-drawer class to reflect non-empty state
                    drawerContainer.classList.toggle('is-empty', false);
                    
                    // Show the footer when cart has items
                    this.showCartFooter();
                  }
                  
                  // Update cart footer (main cart page)
                  this.updateCartFooter(cartFooterHTML);
                  
                  // Update header cart icon
                  this.updateCartIcon(cartIconHTML);
                  
                  // Re-initialize any components that might need it
                  this.reinitializeCartComponents();
                })
                .catch(error => {
                  console.error('Error fetching cart state:', error);
                  // Fallback to full update if we can't determine cart state
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = drawerHTML;
                  const newDrawerInner = tempDiv.querySelector('.drawer__inner');
                  const existingDrawerInner = drawerContainer.querySelector('.drawer__inner');
                  if (newDrawerInner && existingDrawerInner) {
                    existingDrawerInner.replaceWith(newDrawerInner);
                  }
                  this.reinitializeCartComponents();
                });
            }
          })
          .catch((error) => console.error('Error updating cart drawer:', error));
      }

      updateCartFooterAndHeader() {
        // Update cart footer and header cart icon separately
        fetch('/?sections=cart-icon-bubble,main-cart-footer')
          .then((response) => response.json())
          .then((data) => {
            const cartIconHTML = data['cart-icon-bubble'];
            const cartFooterHTML = data['main-cart-footer'];
            
            this.updateCartFooter(cartFooterHTML);
            this.updateCartIcon(cartIconHTML);
          })
          .catch((error) => console.error('Error updating cart footer and header:', error));
      }

      updateCartFooter(cartFooterHTML) {
        if (!cartFooterHTML) return;
        
        const cartFooter = document.querySelector('#main-cart-footer');
        if (cartFooter) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = cartFooterHTML;
          const newCartFooter = tempDiv.querySelector('#main-cart-footer');
          if (newCartFooter) {
            cartFooter.replaceWith(newCartFooter);
          }
        }
      }

      updateCartIcon(cartIconHTML) {
        if (!cartIconHTML) return;
        
        const cartIcon = document.querySelector('#cart-icon-bubble');
        if (cartIcon) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = cartIconHTML;
          const newCartIcon = tempDiv.querySelector('#cart-icon-bubble');
          if (newCartIcon) {
            cartIcon.replaceWith(newCartIcon);
          }
        }
      }

      reinitializeCartComponents() {
        // Re-initialize cart drawer components that might need it
        setTimeout(() => {
          // Re-initialize cart slider if it exists
          if (window.initCartSlider && typeof window.initCartSlider === 'function') {
            window.initCartSlider();
          }
          
          // Re-attach event listeners for cart drawer
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.setupModalObserver === 'function') {
            cartDrawer.setupModalObserver();
          }
        }, 100);
      }

      showSuccessPopup(productData, cartData) {
        // Get the selected variant details
        const selectedVariant = this.getSelectedVariant(productData);
        const imageUrl = this.getSelectedVariantImage(productData);
        
        // Create success popup HTML
        const successHTML = `
          <div class="quick-add-success-popup">
          <div class="quick-add-success-bar"><svg role="presentation" focusable="false" stroke-width="2" width="18" height="18" class="offset-icon icon icon-success" style="--icon-height: 18px" viewBox="0 0 18 18">
            <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
            <path d="M5 8.8L7.62937 11.6L13 6" stroke="#ffffff" fill="none"></path>
            </svg>Added to your cart!
          </div>
            <div class="quick-add-success-header">
              <div class="quick-add-success-image">
                ${imageUrl ? `<img src="${imageUrl}" alt="${productData.title}" loading="lazy">` : ''}
              </div>
              <div class="quick-add-success-details">
                <h3 class="quick-add-success-title">${productData.title}</h3>
                <div class="quick-add-success-variant">
                  ${this.getSelectedVariantText(productData)}
                </div>
                <div class="quick-add-success-price">
                  <span class="price-item price-item--regular">
                    ${this.formatMoney(selectedVariant.price)}
                  </span>
                  ${selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price ? 
                    `<span class="price-item price-item--sale">
                      ${this.formatMoney(selectedVariant.compare_at_price)}
                    </span>` : ''
                  }
                </div>
              </div>
            </div>
            <div class="quick-add-success-actions">
              <button class="quick-add-view-cart" type="button" onclick="window.location.href='/cart'">
                View Cart
              </button>
              <button class="quick-add-continue-shopping" type="button" onclick="window.location.href='/checkout'">
                Checkout
              </button>
            </div>
          </div>
        `;

        // Replace the modal content with success popup
        HTMLUpdateUtility.setInnerHTML(this.modalContent, successHTML);
        this.positionToggleButton();
        this.classList.add('has-success-popup');
      }

      getSelectedVariant(productData) {
        const selectedSwatches = this.modalContent.querySelectorAll('.quick-add-swatch.selected');
        const currentSelections = [];
        
        selectedSwatches.forEach(swatch => {
          const optionIndex = parseInt(swatch.dataset.optionIndex);
          const value = swatch.dataset.value;
          currentSelections[optionIndex] = value;
        });

        return productData.variants.find(variant => {
          return variant.options.every((option, index) => {
            return currentSelections[index] === option;
          });
        }) || productData.variants[0];
      }

      getSelectedVariantImage(productData) {
        const selectedVariant = this.getSelectedVariant(productData);
        
        // Try to get variant-specific image
        if (selectedVariant.featured_image || selectedVariant.image) {
          return this.extractImageUrl(selectedVariant.featured_image || selectedVariant.image);
        }
        
        // Fallback to first product image
        if (productData.images && productData.images.length > 0) {
          return this.extractImageUrl(productData.images[0]);
        }
        
        return null;
      }

      getSelectedVariantText(productData) {
        const selectedVariant = this.getSelectedVariant(productData);
        const selectedSwatches = this.modalContent.querySelectorAll('.quick-add-swatch.selected');
        
        if (selectedSwatches.length === 0) {
          return '';
        }
        
        const variantTexts = [];
        selectedSwatches.forEach(swatch => {
          const optionIndex = parseInt(swatch.dataset.optionIndex);
          const optionName = productData.options[optionIndex]?.name || '';
          const value = swatch.dataset.value;
          if (optionName && value) {
            variantTexts.push(`${optionName}: ${value}`);
          }
        });
        
        return variantTexts.join(' • ');
      }

      hideVariantOptions() {
        const optionsContainer = this.modalContent.querySelector('.quick-add-product-options');
        if (optionsContainer) {
          optionsContainer.style.display = 'none';
        }
      }

      showCartNotification(productData) {
        // Show cart notification
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification) {
          cartNotification.show();
        }
      }

      positionToggleButton() {
        // Only position toggle button on desktop
        if (window.innerWidth <= 640) return;
        
        const toggleButton = this.querySelector('.quick-add-modal__toggle');
        const modalContent = this.querySelector('.quick-add-modal__content');
        
        if (!toggleButton || !modalContent) return;
        
        // Get modal content position and dimensions
        const modalRect = modalContent.getBoundingClientRect();
        const toggleRect = toggleButton.getBoundingClientRect();
        
        // Calculate position: center of modal horizontally, top edge of modal minus toggle height
        const newTop = modalRect.top - toggleRect.height - 10; // 10px gap
        const modalCenterX = modalRect.left + (modalRect.width / 2);
        const toggleCenterX = toggleRect.width / 2;
        const newLeft = modalCenterX - toggleCenterX;
        
        // Apply new position
        toggleButton.style.position = 'fixed';
        toggleButton.style.top = `${newTop}px`;
        toggleButton.style.left = `${newLeft}px`;
        toggleButton.style.right = 'auto';
        toggleButton.style.transform = 'none';
        
        // Add responsive adjustments for different screen sizes
        if (window.innerWidth >= 1200) {
          // Large desktop - position slightly higher
          toggleButton.style.top = `${newTop - 20}px`;
        } else if (window.innerWidth >= 900) {
          // Medium desktop - standard position
          toggleButton.style.top = `${newTop}px`;
        } else {
          // Small desktop - position slightly lower
          toggleButton.style.top = `${newTop + 10}px`;
        }
      }

      resetToggleButtonPosition() {
        const toggleButton = this.querySelector('.quick-add-modal__toggle');
        if (toggleButton) {
          // Reset to default CSS position
          toggleButton.style.position = '';
          toggleButton.style.top = '';
          toggleButton.style.left = '';
          toggleButton.style.right = '';
          toggleButton.style.transform = '';
        }
      }

      hideEmptyCartDiv() {
        // Hide or remove the drawer__inner-empty div when cart has items
        const emptyCartDiv = document.querySelector('.drawer__inner-empty');
        if (emptyCartDiv) {
          emptyCartDiv.style.display = 'none';
        }
      }

      hideEmptyCartElements() {
        // Hide all empty cart related elements
        const emptyElements = document.querySelectorAll('.drawer__inner-empty, .cart-drawer__empty, .cart-drawer__empty-message');
        emptyElements.forEach(element => {
          element.style.display = 'none';
        });
      }

      showCartFooter() {
        // Show cart footer when cart has items
        const cartFooter = document.querySelector('.cart-drawer__footer');
        if (cartFooter) {
          cartFooter.style.display = 'block';
        }
      }

      // Method to reinitialize quick add modals after cart drawer updates
      reinitializeQuickAddModals() {
        console.log('Reinitializing quick add modals after cart drawer update');
        
        // Find all quick add buttons and reattach event listeners
        const quickAddButtons = document.querySelectorAll('[data-product-url]');
        quickAddButtons.forEach(button => {
          // Remove existing event listeners
          if (button._quickAddHandler) {
            button.removeEventListener('click', button._quickAddHandler);
            delete button._quickAddHandler;
          }
          
          // Add new event listener
          button.addEventListener('click', (event) => {
            const modal = document.querySelector('quick-add-modal');
            if (modal && button.getAttribute('data-product-url')) {
              modal.show(button);
            }
          });
        });
      }
    }
  );
}